import { useQueryClient } from '@tanstack/react-query';
import useAppModal from 'hooks/useAppModal';
import useAppMutation from 'hooks/useAppMutation';
import { PropertyInternalFee } from 'models/PropertyFees';
import API from 'services/API';
import useNotify from 'hooks/useNotify';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { useTranslation } from 'react-i18next';
import { PROPERTIES_FEES_BASE_QUERY_KEY } from '../PropertyFeesAndTaxes.utils';
import {
  ADD_PROPERTY_FEE_MODAL_ID,
  EDIT_PROPERTY_FEE_MODAL_ID,
} from './PropertyFeeModal';

export const UPDATE_PROPERTIES_FEES_BASE_QUERY_KEY = 'updatePropertyFees';
export const ADD_PROPERTIES_FEES_BASE_QUERY_KEY = 'createPropertyFees';

interface BulkCreatePropertyFeesPayload
  extends Omit<
    PropertyInternalFee,
    | 'metadata'
    | 'uid'
    | 'propertyUid'
    | 'optional'
    | 'longTermStayExemption'
    | 'attestation'
  > {
  allProperties: boolean;
  propertiesUids: string[];
}

interface BulkUpdatePropertyFeesPayload extends BulkCreatePropertyFeesPayload {
  uid: PropertyInternalFee['uid'];
}

enum DuplicatedMappingError {
  NON_UNIQUE_VRBO_TYPE = 'NON_UNIQUE_VRBO_TYPE',
  NON_UNIQUE_BCOM_TYPE = 'NON_UNIQUE_BCOM_TYPE',
  NON_UNIQUE_AIRBNB_TYPE = 'NON_UNIQUE_AIRBNB_TYPE',
}

const channelNameMap = {
  [DuplicatedMappingError.NON_UNIQUE_VRBO_TYPE]: 'Vrbo',
  [DuplicatedMappingError.NON_UNIQUE_BCOM_TYPE]: 'Booking.com',
  [DuplicatedMappingError.NON_UNIQUE_AIRBNB_TYPE]: 'Airbnb',
};

const usePropertyFeeModalQueries = () => {
  const queryClient = useQueryClient();
  const { closeModal } = useAppModal();
  const { notifyError } = useNotify();
  const { t } = useTranslation();

  const isDuplicateMappingError = (errorCode: string) => {
    return Object.values(DuplicatedMappingError).includes(
      errorCode as DuplicatedMappingError,
    );
  };

  const formatErrorWithProperties = (errorCode: string, properties: any[]) => {
    const maxVisible = 3;
    const visibleProperties = properties.slice(0, maxVisible);
    const hasMore = properties.length > maxVisible;
    const propertyNames =
      visibleProperties.map((p) => p.propertyName).join(', ') +
      (hasMore ? '...' : '');

    return t(
      'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.duplicatedMappingTypeError',
      {
        channelName: channelNameMap[errorCode as DuplicatedMappingError],
        properties: propertyNames,
      },
    );
  };

  const { mutateAsync: createFee, isLoading: isCreatingFee } = useAppMutation(
    [ADD_PROPERTIES_FEES_BASE_QUERY_KEY],
    (payload: BulkCreatePropertyFeesPayload) => {
      return API.post('/api/internal/fees', payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PROPERTIES_FEES_BASE_QUERY_KEY]);
        closeModal(ADD_PROPERTY_FEE_MODAL_ID);
      },
      onError: (error: any) => {
        const errorCode = error.response.data?.apiErrorCode;
        const metadata = error.response.data?.metadata;

        if (
          isDuplicateMappingError(errorCode) &&
          metadata?.properties?.length > 0
        ) {
          notifyError(
            formatErrorWithProperties(errorCode, metadata.properties),
          );
          return;
        }

        notifyError(
          getErrorMessage({
            apiErrorCode: errorCode,
            apiErrorMessage: error.response.data?.apiErrorMessage,
            baseKey: 'pageProperty.apiErrorCodes.',
            t,
          }),
        );
      },
    },
  );

  const { mutateAsync: updateFee, isLoading: isUpdatingFee } = useAppMutation(
    [UPDATE_PROPERTIES_FEES_BASE_QUERY_KEY],
    (payload: BulkUpdatePropertyFeesPayload) => {
      const { uid, ...values } = payload;

      return API.patch(`/api/internal/fees/${uid}`, values);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PROPERTIES_FEES_BASE_QUERY_KEY]);
        closeModal(EDIT_PROPERTY_FEE_MODAL_ID);
      },
      onError: (error: any) => {
        const errorCode = error.response.data?.apiErrorCode;
        const metadata = error.response.data?.metadata;

        if (
          isDuplicateMappingError(errorCode) &&
          metadata?.properties?.length > 0
        ) {
          notifyError(
            formatErrorWithProperties(errorCode, metadata.properties),
          );
          return;
        }

        notifyError(
          getErrorMessage({
            apiErrorCode: errorCode,
            apiErrorMessage: error.response.data?.apiErrorMessage,
            baseKey: 'pageProperty.apiErrorCodes.',
            t,
          }),
        );
      },
    },
  );

  return {
    createFee,
    isCreatingFee,
    updateFee,
    isUpdatingFee,
  };
};

export default usePropertyFeeModalQueries;
